<script setup lang="ts">
  import {
    type SuperUserOrganisation as Organisation,
    type PatchedSuperUserOrganisationRequest as PatchedOrganisationRequest,
    type StripeCustomersListResponse,
    type SuperUserStripeCustomer as StripeCustomer,
    type LawFields,
    type HubspotCompaniesListResponse,
    type SuperUserHubSpotCompany as HubSpotCompany
  } from '@/types/generated/types.gen'
  import {
    OrganisationsService,
    StripeCustomersService,
    LawFieldsService,
    HubspotService
  } from '@/types/generated/services.gen'
  import { ref, onMounted, computed } from 'vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import router from '@/router'
  import { type GenericObject } from 'vee-validate'
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import * as yup from 'yup'
  import { useTranslation } from '@/composables/useTranslation'
  import { $SuperUserOrganisation as $Organisation } from '@/types/generated'
  import FormGrid from '@/components/admin/form/FormGrid.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import MultiSelectField from '@/components/admin/form/MultiSelectField.vue'

  const { t } = useTranslation({
    en: {
      general: 'General',
      features: 'Features',

      name: 'Lawirm Name',
      street: 'Street',
      house: 'House',
      postcode: 'Postcode',
      city: 'City',
      country: 'Country',
      email: 'Email',
      activateAI: 'Activate AI',
      activateAIAnsweringMachine: 'Activate AI Answering Machine',
      activateCases: 'Activate Cases',
      jupusEmail: 'Jupus Email',
      emailResponseFromClients: 'Email Response From Clients',
      phone: 'Phone',
      seats: 'Seats',
      appointmentBooking: 'Appointment Booking',
      appointmentBookingSeats: 'Appointment Booking Seats',
      legalAreas: 'Legal Areas',
      connections: 'Connections',
      stripeConnection: 'Stripe Connection',
      lawFields: 'Law Fields',
      emailNotifications: 'Email Notifications',
      receiveQuestionnaireClosedMail: 'Receive email when a questionnaire is closed',
      receiveDocumentSignedMail: 'Receive email when a document is signed',
      receiveNewCaseMail: 'Receive email when a new case is created',
      receiveNewCaseEmailMail: 'Receive email when a new email is received for a case',
      receiveNewAppointmentMail: 'Receive email when a new appointment is created',
      sendDocumentEmailReminders: 'Send automated email reminders',
      sendDocumentEmailRemindersEveryXDays: 'Send email reminders every X days',
      sendEmailClientConfirmationOnNewCase: 'Send email confirmation to client on new case',
      sendEmailClientConfirmationOnNewCaseViaEmail:
        'Send email confirmation to client on new case via email',
      receiveClerkChangeMail: 'Receive email when clerk changes',
      notificationsMonthlySum: 'Receive monthly summary of notifications',
      hubspotCompanies: 'HubSpot Connection',
      hasChurned: 'Has Churned'
    },
    de: {
      general: 'Allgemein',
      features: 'Features',
      name: 'Name der Kanzlei',
      street: 'Straße',
      house: 'Haus',
      postcode: 'PLZ',
      city: 'Stadt',
      country: 'Land',
      email: 'Email',
      activateAI: 'KI aktivieren',
      activateAIAnsweringMachine: 'KI-Anrufbeantworter aktivieren',
      activateCases: 'JUPUS Anfragen aktivieren',
      jupusEmail: 'JUPUS Email',
      emailResponseFromClients: 'E-Mail Antwort von Mandanten in JUPUS',
      phone: 'Telefon',
      seats: 'Seats',
      appointmentBooking: 'Terminbuchung',
      appointmentBookingSeats: 'Terminbuchung Seats',
      legalAreas: 'Rechtsgebiete',
      connections: 'Verbindungen',
      stripeConnection: 'Stripe Verbindung',
      lawFields: 'Rechtsgebiete',
      emailNotifications: 'E-Mail-Benachrichtigungen',
      receiveQuestionnaireClosedMail: 'E-Mail erhalten, wenn ein Fragebogen abgeschlossen wird',
      receiveDocumentSignedMail: 'E-Mail erhalten, wenn ein Dokument unterschrieben wird',
      receiveNewCaseMail: 'E-Mail erhalten, wenn ein neuer Fall erstellt wird',
      receiveNewCaseEmailMail: 'E-Mail erhalten, wenn eine neue E-Mail zu einem Fall eingeht',
      receiveNewAppointmentMail: 'E-Mail erhalten, wenn ein neuer Termin erstellt wird',
      sendDocumentEmailReminders: 'Automatisierte E-Mail-Erinnerungen senden',
      sendDocumentEmailRemindersEveryXDays: 'E-Mail-Erinnerungen alle X Tage senden',
      sendEmailClientConfirmationOnNewCase: 'E-Mail-Bestätigung an Mandanten bei neuem Fall senden',
      sendEmailClientConfirmationOnNewCaseViaEmail:
        'E-Mail-Bestätigung an Mandanten bei neuem Fall per E-Mail senden',
      receiveClerkChangeMail: 'E-Mail erhalten, wenn sich der Sachbearbeiter ändert',
      notificationsMonthlySum: 'Monatliche Zusammenfassung der Benachrichtigungen erhalten',
      hubspotCompanies: 'HubSpot Verbindung',
      hasChurned: 'Hat gekündigt'
    }
  })

  const org = ref<Organisation | undefined>(undefined)
  const stripeCustomersList = ref<StripeCustomersListResponse>()
  const stripeCustomersOptions = computed(() => {
    return (
      stripeCustomersList.value?.results.map((customer) => ({
        value: customer.uuid,
        title: customer.name?.toString() + ' - ' + customer.email?.toString()
      })) ?? []
    )
  })
  const stripeCustomer = computed<StripeCustomer>(() => {
    return (
      stripeCustomersList.value?.results.find(
        (customer) => customer.uuid === org.value?.stripe_customer_uuid
      ) ?? {}
    )
  })
  const hubspotCompaniesList = ref<HubspotCompaniesListResponse>()
  const hubspotCompaniesOptions = computed(() => {
    // Map the HubSpot companies associated with the organization
    const associatedCompanies = org.value?.hubspot_company
      ? [
          {
            value: org.value.hubspot_company.uuid,
            title: org.value.hubspot_company.name?.toString()
          }
        ]
      : []

    // Map all available HubSpot companies from the API call
    const availableCompanies =
      hubspotCompaniesList.value?.results.map((company) => ({
        value: company.uuid,
        title: company.name?.toString()
      })) ?? []

    // Merge associated and available companies, avoiding duplicates
    const mergedCompanies = [...associatedCompanies, ...availableCompanies].reduce(
      (acc, current) => {
        if (!acc.some((item) => item.value === current.value)) {
          acc.push(current)
        }
        return acc
      },
      []
    )
    console.log('companies options', mergedCompanies)
    return mergedCompanies
  })

  const lawFields = ref<LawFields>()
  const lawFieldOptions = computed(() => {
    return (
      Object.keys(lawFields.value?.data ?? {}).map((key) => ({
        value: key,
        title: key
      })) ?? []
    )
  })

  onMounted(async () => {
    org.value = (await OrganisationsService.organisationsRetrieve({
      uuid: router.currentRoute.value.params.uuid.toString()
    })) as Organisation
    stripeCustomersList.value = await StripeCustomersService.stripeCustomersList({ limit: 0 })
    lawFields.value = await LawFieldsService.lawFieldsRetrieve()
    hubspotCompaniesList.value = await HubspotService.hubspotCompaniesList({ limit: 10 })
  })

  const hubspotCompaniesInputAction = async (newVal: string, oldVal: string) => {
    if (newVal !== oldVal) {
      hubspotCompaniesList.value = await HubspotService.hubspotCompaniesList({
        search: newVal,
        limit: 10
      })
    }
  }

  const handleSave = async (values: GenericObject) => {
    console.log('handleSave called', values)
    OrganisationsService.organisationsPartialUpdate({
      uuid: org.value!.uuid,
      requestBody: values as PatchedOrganisationRequest
    })
  }
</script>

<template>
  <div
    v-if="org"
    class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5"
  >
    <div class="flex items-center justify-between gap-x-5">
      <Breadcrumbs
        :items="[
          { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
          { title: 'Customers', to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_LIST },
          {
            title: `Edit ${org.uuid}`,
            to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_LIST.replace(':uuid', org.uuid)
          }
        ]"
      />
    </div>

    <div
      class="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-700
        dark:bg-neutral-800"
    >
      <AutoSavingForm @save="handleSave">
        <div class="grid grid-cols-3 gap-8">
          <div class="col-span-2">
            <div class="mb-4 font-semibold">
              {{ t('general') }}
            </div>
            <hr class="mb-4" />
            <InputField
              v-model="org.name"
              :label="t('name')"
              name="name"
              :rules="yup.string().required()"
            />
            <FormGrid>
              <div class="col-span-9">
                <InputField
                  v-model="org.street"
                  :label="t('street')"
                  name="street"
                  :rules="yup.string().required()"
                />
              </div>
              <div class="col-span-3">
                <InputField
                  v-model="org.street_number"
                  :label="t('house')"
                  name="street_number"
                  :rules="yup.string().max($Organisation.properties.street.maxLength).required()"
                />
              </div>
            </FormGrid>
            <FormGrid>
              <div class="col-span-3">
                <InputField
                  v-model="org.zip"
                  :label="t('postcode')"
                  name="zip"
                  :rules="yup.string().max($Organisation.properties.zip.maxLength).required()"
                />
              </div>
              <div class="col-span-9">
                <InputField
                  v-model="org.city"
                  :label="t('city')"
                  name="city"
                  :rules="yup.string().max($Organisation.properties.city.maxLength).required()"
                />
              </div>
            </FormGrid>
            <InputField
              v-model="org.country"
              :label="t('country')"
              name="country"
              :rules="yup.string().max($Organisation.properties.country.maxLength).required()"
            />
            <InputField
              v-model="org.email"
              :label="t('email')"
              name="email"
              :rules="yup.string().email().required()"
            />
            <InputField
              v-model="org.phone"
              :label="t('phone')"
              name="phone"
              :rules="yup.string().max($Organisation.properties.phone.maxLength).required()"
            />
            <MultiSelectField
              v-model="org.law_fields"
              :options="lawFieldOptions"
              name="law_fields"
              :label="t('lawFields')"
            />
          </div>
          <div class="col-span-1">
            <div class="pb-3 text-base font-semibold text-gray-800 sm:text-lg">
              {{ t('connections') }}
            </div>
            <hr class="mb-4" />
            <SelectField
              v-model="org.stripe_customer_uuid"
              name="stripe_customer_uuid"
              :label="t('stripeConnection')"
              :is-filterable="true"
              :options="stripeCustomersOptions"
              :rules="yup.string().uuid().notRequired()"
            />
            <a
              v-if="stripeCustomer"
              target="_blank"
              class="text-xs text-purple-600"
              :href="stripeCustomer.stripe_url"
            >
              {{ stripeCustomer.stripe_url }}
            </a>
            <SelectField
              v-model="org.hubspot_company_uuid"
              :options="hubspotCompaniesOptions"
              :input-action="hubspotCompaniesInputAction"
              name="hubspot_company_uuid"
              :label="t('hubspotCompanies')"
              :is-filterable="true"
            />
            <a
              v-if="org.hubspot_company"
              target="_blank"
              class="text-xs text-purple-600"
              :href="org.hubspot_company.hubspot_url"
            >
              {{ org.hubspot_company.hubspot_url }}
            </a>
            <div class="pb-3 pt-5 text-base font-semibold text-gray-800 sm:text-lg">
              {{ t('seats') }}
            </div>
            <hr class="mb-4" />
            <InputField
              v-model="org.seats"
              type="number"
              :label="t('seats')"
              name="seats"
              :rules="yup.number().required()"
            />

            <InputField
              v-model="org.appointments_seats"
              :label="t('appointmentBookingSeats')"
              name="appointments_seats"
              :rules="yup.number().required()"
            />
            <CheckboxField
              v-model="org.has_churned"
              :label="t('hasChurned')"
              name="has_churned"
            />
            <div class="pb-3 pt-5 text-base font-semibold text-gray-800 sm:text-lg">
              {{ t('features') }}
            </div>
            <hr class="mb-4" />
            <CheckboxField
              v-model="org.ai_enabled"
              :label="t('activateAI')"
              name="ai_enabled"
            />
            <CheckboxField
              v-model="org.enable_cases"
              :label="t('activateCases')"
              name="enable_cases"
            />
            <CheckboxField
              v-model="org.enable_voice"
              :label="t('activateAIAnsweringMachine')"
              name="enable_voice"
            />
            <CheckboxField
              v-model="org.enable_inbound_email_replies"
              :label="t('emailResponseFromClients')"
              name="enable_inbound_email_replies"
            />
            <CheckboxField
              v-model="org.enable_appointments"
              :label="t('appointmentBooking')"
              name="enable_appointments"
            />
          </div>
        </div>
        <div class="col-span-3 mt-8">
          <div
            class="rounded-lg border border-gray-200 bg-gray-50 p-6 dark:border-neutral-700 dark:bg-neutral-900"
          >
            <div class="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-200">
              {{ t('emailNotifications') }}
            </div>
            <hr class="mb-6 border-gray-300 dark:border-neutral-600" />
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <CheckboxField
                v-model="org.receive_questionnaire_closed_mail"
                :label="t('receiveQuestionnaireClosedMail')"
                name="receive_questionnaire_closed_mail"
              />
              <CheckboxField
                v-model="org.receive_document_signed_mail"
                :label="t('receiveDocumentSignedMail')"
                name="receive_document_signed_mail"
              />
              <CheckboxField
                v-model="org.receive_new_case_mail"
                :label="t('receiveNewCaseMail')"
                name="receive_new_case_mail"
              />
              <CheckboxField
                v-model="org.receive_new_case_email_mail"
                :label="t('receiveNewCaseEmailMail')"
                name="receive_new_case_email_mail"
              />
              <CheckboxField
                v-model="org.receive_new_appointment_mail"
                :label="t('receiveNewAppointmentMail')"
                name="receive_new_appointment_mail"
              />
              <CheckboxField
                v-model="org.send_document_email_reminders"
                :label="t('sendDocumentEmailReminders')"
                name="send_document_email_reminders"
              />
              <div class="col-span-1 sm:col-span-2">
                <InputField
                  v-model="org.send_document_email_reminders_every_x_days"
                  :label="t('sendDocumentEmailRemindersEveryXDays')"
                  name="send_document_email_reminders_every_x_days"
                  type="number"
                  :rules="yup.number().min(1).max(30).required()"
                />
              </div>
              <CheckboxField
                v-model="org.send_email_client_confirmation_on_new_case"
                :label="t('sendEmailClientConfirmationOnNewCase')"
                name="send_email_client_confirmation_on_new_case"
              />
              <CheckboxField
                v-model="org.send_email_client_confirmation_on_new_case_via_email"
                :label="t('sendEmailClientConfirmationOnNewCaseViaEmail')"
                name="send_email_client_confirmation_on_new_case_via_email"
              />
              <CheckboxField
                v-model="org.receive_clerk_change_mail"
                :label="t('receiveClerkChangeMail')"
                name="receive_clerk_change_mail"
              />
              <CheckboxField
                v-model="org.receive_monthly_updates_summary"
                :label="t('notificationsMonthlySum')"
                name="notifications_monthly_summary"
              />
            </div>
          </div>
        </div>
      </AutoSavingForm>
    </div>
  </div>
</template>

<style scoped></style>
